import { ButtonVariant, Menu, MenuItem, Tooltip } from "@merge-api/merge-javascript-shared";
import {
  BlueprintVersion,
  IntegrationEditorAction,
  IntegrationEditorTopControlPanelActionProps,
} from "../../../models/Blueprints";
import { useBlueprintActions } from "./hooks/useBlueprintActions";
import clsx from "clsx";

interface IntegrationEditorTopControlComboButtonProps {
  publishedStateActions: IntegrationEditorTopControlPanelActionProps[];
  blueprintVersion?: BlueprintVersion;
  isArchived?: boolean;
  isEnvironmentHighlighted?: boolean;
  isIntegrationInCanaryTesting?: boolean;
  shouldShowStageDisabledTooltip?: boolean;
}

const IntegrationEditorTopControlComboButton = ({
  publishedStateActions,
  blueprintVersion,
  isArchived,
  isEnvironmentHighlighted,
  isIntegrationInCanaryTesting,
  shouldShowStageDisabledTooltip,
}: IntegrationEditorTopControlComboButtonProps) => {
  const { mainAction, dropdownActions } = useBlueprintActions({
    publishedStateActions,
    blueprintVersion,
    isArchived,
    isIntegrationInCanaryTesting,
  });

  if (!mainAction || !blueprintVersion) return null;

  // When Stage is loading, disable combo mode to prevent loading spinner on dropdown trigger
  const shouldShowStageLoading =
    mainAction.text === IntegrationEditorAction.STAGE &&
    publishedStateActions.find((action) => action.text === IntegrationEditorAction.STAGE)
      ?.isLoading;
  const shouldShowMainActionTooltip =
    mainAction.text === IntegrationEditorAction.STAGE &&
    !!mainAction.isDisabled &&
    !!mainAction.disabledTooltipText &&
    !!shouldShowStageDisabledTooltip;

  const menu = (
    <Menu
      isComboButton={!shouldShowStageLoading}
      menuPlacement="bottom-end"
      ButtonProps={{
        disabled: mainAction.isDisabled,
        variant: ButtonVariant.PrimaryGray,
        className: isEnvironmentHighlighted
          ? "bg-orange-30 text-black ring-1 ring-orange-40 hover:enabled:bg-orange-40 focus-visible:bg-orange-30 disabled:bg-orange-10 disabled:text-gray-70 disabled:ring-orange-30"
          : undefined,
        children: mainAction.text,
        onClick: mainAction.onClick,
        loading: shouldShowStageLoading,
      }}
    >
      {dropdownActions.map((action) => {
        const isDeleteAction = action.text === IntegrationEditorAction.DELETE;

        const menuItem = (
          <MenuItem
            key={action.text}
            onClick={action.onClick}
            disabled={action.isDisabled}
            className={clsx(isDeleteAction && !action.isDisabled && "text-red-60")}
          >
            {action.text}
          </MenuItem>
        );

        const shouldShowActionTooltip =
          action.isDisabled &&
          action.disabledTooltipText &&
          (action.text !== IntegrationEditorAction.STAGE || shouldShowStageDisabledTooltip);

        return shouldShowActionTooltip ? (
          <div className="flex items-start flex-col">
            <Tooltip placement="left" title={action.disabledTooltipText} key={action.text}>
              {menuItem}
            </Tooltip>
          </div>
        ) : (
          menuItem
        );
      })}
    </Menu>
  );

  return shouldShowMainActionTooltip ? (
    <Tooltip title={mainAction.disabledTooltipText}>
      <div className="flex items-center">{menu}</div>
    </Tooltip>
  ) : (
    menu
  );
};

export default IntegrationEditorTopControlComboButton;
